import { useState } from "react";
import { Link, NavLink, useNavigate } from "../../lib/i18n";
import { AnimatePresence, motion } from "framer-motion";
import { Search, User, Heart, ShoppingBag, Menu, Phone, Truck, ArrowRight, Calculator } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../context/SiteContext";
import { SearchDialog } from "./SearchDialog";

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Bois de chauffage", to: "/boutique?univers=bois", mega: "bois" },
  { label: "Poêles & inserts", to: "/poeles", mega: "poeles" },
  { label: "Granulés", to: "/granules" },
  { label: "Accessoires", to: "/accessoires", mega: "accessoires" },
  { label: "Packs & offres", to: "/packs" },
  { label: "Conseils", to: "/conseils" },
  { label: "Livraison", to: "/livraison" },
  { label: "À propos", to: "/a-propos" },
  { label: "Contact", to: "/contact" },
];

export const Logo = ({ light = false }) => {
  const { site } = useSite();
  if (site?.logo_url) return <img src={site.logo_url} alt={site.brand_name} className="h-10 w-auto" />;
  return (
    <span className="flex items-center gap-3">
      <span className={`grid h-10 w-10 place-items-center rounded-full border ${light ? "border-laiton/50" : "border-bois/40"}`}>
        <svg viewBox="0 0 24 24" className="h-5 w-5 text-laiton" fill="currentColor"><path d="M12 2c1 4 5 6 5 11a5 5 0 0 1-10 0c0-2.5 1.5-4 2.5-5.5C10 10 11 11 12 11c0-3-1-6 0-9Z" /></svg>
      </span>
      <span className="leading-none">
        <span className={`block whitespace-nowrap font-serif text-lg sm:text-[22px] font-semibold tracking-tight ${light ? "text-ivoire" : "text-brun"}`}>{site?.brand_name || "Maison de braise"}</span>
        <span className={`hidden sm:block text-[9px] uppercase tracking-[0.3em] ${light ? "text-ivoire/60" : "text-pierre"}`}>Chauffage au bois</span>
      </span>
    </span>
  );
};

function Mega({ universe, onClose }) {
  const { categories = [] } = useSite();
  const cats = categories.filter((c) => c.universe === universe || (universe === "bois" && c.universe === "granules"));
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
      className="absolute left-0 right-0 top-full border-t border-beige bg-ivoire shadow-[0_30px_60px_rgba(42,24,16,0.10)]" data-testid={`mega-menu-${universe}`}>
      <div className="container-x grid grid-cols-12 gap-10 py-10">
        <div className="col-span-8 grid grid-cols-2 gap-6">
          {cats.map((c) => (
            <Link key={c.slug} to={`/boutique/${c.slug}`} onClick={onClose} className="group flex gap-4 rounded-md p-2 transition-colors hover:bg-creme" data-testid={`mega-cat-${c.slug}`}>
              <img src={c.image} alt="" className="h-20 w-24 rounded object-cover" loading="lazy" />
              <span>
                <span className="block font-serif text-xl text-brun group-hover:text-bois">{c.name}</span>
                <span className="mt-1 block text-xs leading-relaxed text-pierre">{c.description}</span>
              </span>
            </Link>
          ))}
        </div>
        <div className="col-span-4 rounded-lg bg-brun p-8 text-ivoire">
          <Calculator className="text-laiton" />
          <p className="mt-4 font-serif text-2xl !text-ivoire">De combien de bois avez-vous besoin ?</p>
          <p className="mt-2 text-sm text-ivoire/70">Estimez votre consommation annuelle en moins d'une minute.</p>
          <Link to="/calculateur" onClick={onClose} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-laiton link-u">Lancer le calculateur <ArrowRight size={14} /></Link>
        </div>
      </div>
    </motion.div>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger className="xl:hidden p-2 -ml-2" data-testid="mobile-menu-btn" aria-label="Menu"><Menu size={22} className="text-brun" /></SheetTrigger>
      <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto bg-ivoire p-0">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="border-b border-beige p-6"><Logo /></div>
        <nav className="p-4">
          <Accordion type="single" collapsible>
            {NAV.map((n) => (
              <div key={n.label} className="border-b border-beige/70">
                <Link to={n.to} onClick={() => setOpen(false)} className="block py-4 font-serif text-xl text-brun" data-testid={`mobile-nav-${n.to}`}>{n.label}</Link>
              </div>
            ))}
            <AccordionItem value="x" className="border-0" />
          </Accordion>
          <div className="mt-6 grid gap-3">
            <Link to="/calculateur" onClick={() => setOpen(false)} className="btn-ghost">Calculateur de bois</Link>
            <Link to="/devis" onClick={() => setOpen(false)} className="btn-primary">Demander un devis</Link>
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}


export default function Header() {
  const { count, setOpen, favorites } = useCart();
  const { user, isStaff } = useAuth();
  const { site } = useSite();
  const [mega, setMega] = useState(null);
  const [search, setSearch] = useState(false);
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50" onMouseLeave={() => setMega(null)}>
      <div className="bg-ebene text-ivoire/80">
        <div className="container-x flex h-9 items-center justify-between text-[11px] tracking-wide">
          <span className="flex items-center gap-2 truncate"><Truck size={13} className="text-laiton shrink-0" />{site?.announcement}</span>
          <span className="hidden md:flex items-center gap-6">
            <span className="flex items-center gap-2"><Phone size={12} className="text-laiton" />{site?.contact?.phone}</span>
            <span>{site?.contact?.hours}</span>
          </span>
        </div>
      </div>
      <div className="relative border-b border-beige bg-ivoire/90 backdrop-blur-md">
        <div className="container-x flex h-[72px] items-center justify-between gap-4">
          <div className="flex items-center gap-3"><MobileNav /><Link to="/" data-testid="nav-logo-link"><Logo /></Link></div>
          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
            {NAV.map((n) => (
              <NavLink key={n.label} to={n.to} end onMouseEnter={() => setMega(n.mega || null)} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-[12.5px] font-medium tracking-wide text-encre transition-colors hover:text-bois link-u">{n.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => setSearch(true)} className="p-2 text-brun hover:text-bois transition-colors" data-testid="header-search-btn" aria-label="Rechercher"><Search size={20} /></button>
            <button onClick={() => nav(user ? (isStaff ? "/admin" : "/compte") : "/connexion")} className="p-2 text-brun hover:text-bois transition-colors" data-testid="header-account-btn" aria-label="Compte"><User size={20} /></button>
            <button onClick={() => nav(user ? "/compte/favoris" : "/favoris")} className="relative hidden sm:block p-2 text-brun hover:text-bois" data-testid="header-favorites-btn" aria-label="Favoris">
              <Heart size={20} />{favorites.length > 0 && <span className="absolute right-0.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-bois px-1 text-[9px] text-white">{favorites.length}</span>}
            </button>
            <button onClick={() => setOpen(true)} className="relative p-2 text-brun hover:text-bois" data-testid="mini-cart-drawer-toggle" aria-label="Panier">
              <ShoppingBag size={20} />
              {count > 0 && <motion.span key={count} initial={{ scale: 0.4 }} animate={{ scale: 1 }} className="absolute right-0 top-0 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-laiton px-1 text-[10px] font-bold text-ebene" data-testid="cart-count">{count}</motion.span>}
            </button>
          </div>
        </div>
        <AnimatePresence>{mega && <Mega universe={mega} onClose={() => setMega(null)} />}</AnimatePresence>
      </div>
      <SearchDialog open={search} onOpenChange={setSearch} />
    </header>
  );
}
